import type React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AlertOctagonFill from '@/assets/icons/alert_octagon_fill.svg?component';
import { Button } from '@/components/atoms/Buttons';
import Checkbox from '@/components/atoms/Checkbox';
import FormField from '@/components/atoms/FormField';
import StyledSingleDatePicker from '@/components/atoms/StyledSingleDatePicker/StyledSingleDatePicker';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';
import type { Task } from '@/domain/operationalDashboard';
import { renderTaskTitle } from '@/domain/operationalDashboard';

import styles from './IgnoreTaskModal.module.scss';

interface IgnoreTaskModalProps {
    isOpen: boolean;
    task: Task;
    onClose: () => void;
    onConfirm: (params: {
        taskId: number;
        ignoredUntil?: Date;
        ignoredPermanently?: boolean;
    }) => void;
}

export default function IgnoreTaskModal(props: IgnoreTaskModalProps) {
    const { isOpen, task, onClose, onConfirm } = props;
    const { t } = useTranslation();
    const [ignoredUntil, setIgnoredUntil] = useState<Date | null>(null);
    const [ignoredPermanently, setIgnoredPermanently] = useState(false);

    const handleSubmit = useCallback(
        (ev: React.FormEvent<HTMLFormElement>) => {
            ev.preventDefault();
            onConfirm({
                taskId: task.id,
                ignoredUntil: ignoredPermanently ? undefined : (ignoredUntil ?? undefined),
                ignoredPermanently,
            });
            setIgnoredUntil(null);
            setIgnoredPermanently(false);
            onClose();
        },
        [task.id, ignoredUntil, ignoredPermanently, onConfirm, onClose]
    );

    const handleClose = useCallback(() => {
        setIgnoredUntil(null);
        setIgnoredPermanently(false);
        onClose();
    }, [onClose]);

    const isValid = ignoredPermanently || ignoredUntil != null;

    return (
        <ModalComponent
            className={styles.modalDialog}
            isOpen={isOpen}
            onClose={handleClose}
            keyboard
            centered
            title={t('operationalDashboard.ignoreModal.title')}
            titleIcon={AlertOctagonFill}
            closeTrackingId="operational_ignore_task_close">
            <form onSubmit={handleSubmit}>
                <ModalBody>
                    <div className={styles.taskInfo}>
                        {task.cageName} &middot; {renderTaskTitle(task, t)}
                    </div>
                    <div className={styles.description}>
                        {t('operationalDashboard.ignoreModal.description')}
                    </div>
                    <FormField label={t('operationalDashboard.ignoreModal.untilWhen')}>
                        <StyledSingleDatePicker
                            date={ignoredUntil}
                            onDateChange={setIgnoredUntil}
                            useOutsideRange
                            placeholder={t('operationalDashboard.ignoreModal.datePlaceholder')}
                        />
                    </FormField>
                    <div className={styles.checkboxRow}>
                        <Checkbox
                            label={t('operationalDashboard.ignoreModal.permanently')}
                            labelClassName={styles.checkboxLabel}
                            checked={ignoredPermanently}
                            onCheckedChange={setIgnoredPermanently}
                        />
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button
                        variant="secondary"
                        trackingId="operational_ignore_task_cancel"
                        onClick={handleClose}>
                        {t('common.actions.cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        type="submit"
                        trackingId="operational_ignore_task_submit"
                        disabled={!isValid}>
                        {t('operationalDashboard.ignoreModal.confirm')}
                    </Button>
                </ModalFooter>
            </form>
        </ModalComponent>
    );
}
